import React, { useState, useEffect, useCallback } from "react";
import { Loader2, GripVertical } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { PageHeader } from "./ui";
import { SeverityBadge } from "../components/SeverityBadge";
import { useToast } from "../hooks/use-toast";

const COL_ACCENT = {
  "Backlog": "border-t-slate-400", "To Do": "border-t-blue-400", "In Progress": "border-t-amber-400",
  "In Review": "border-t-violet-400", "Blocked": "border-t-red-500", "Approved": "border-t-teal-500",
  "Done": "border-t-emerald-500", "Closed": "border-t-slate-500",
};

export default function Kanban() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const [statuses, setStatuses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, data] = await Promise.all([api.kanbanConfig(), api.list("tasks", { limit: 2000 })]);
      setStatuses(cfg.statuses);
      setTasks(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDrop = async (status) => {
    setDragOver(null);
    const id = window.__dragTaskId;
    if (!id) return;
    const task = tasks.find((x) => x.id === id);
    if (!task || task.status === status) return;
    const prev = tasks;
    setTasks((ts) => ts.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.kanbanMove(id, status, user?.role || "Chief Security Officer");
    } catch (e) {
      setTasks(prev);
      const msg = e?.response?.data?.detail || "Invalid transition";
      toast({ title: "Move blocked", description: msg });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title={t("kanban")} subtitle={t("tasksByStage")} />
      <div className="flex gap-4 overflow-x-auto pb-4">
        {statuses.map((status) => {
          const colTasks = tasks.filter((x) => x.status === status);
          return (
            <div
              key={status}
              onDragOver={(e) => { e.preventDefault(); setDragOver(status); }}
              onDragLeave={() => setDragOver((s) => (s === status ? null : s))}
              onDrop={() => onDrop(status)}
              className={`flex-shrink-0 w-72 bg-slate-50 rounded-xl border-t-4 ${COL_ACCENT[status] || "border-t-slate-400"} ${dragOver === status ? "ring-2 ring-teal-500 bg-teal-50/50" : ""} transition-colors`}
            >
              <div className="flex items-center justify-between px-3 py-3">
                <span className="font-semibold text-slate-700 text-sm">{status}</span>
                <span className="text-xs font-medium text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{colTasks.length}</span>
              </div>
              <div className="px-2 pb-3 space-y-2 min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto">
                {colTasks.map((task) => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={() => { window.__dragTaskId = task.id; }}
                    onDragEnd={() => { window.__dragTaskId = null; }}
                    className="kanban-card bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-2">
                      <GripVertical className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 leading-snug">{task.title}</p>
                        <p className="text-[11px] text-slate-400 mt-1 font-mono">{task.id}</p>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <SeverityBadge level={task.priority} />
                          <span className="text-[11px] text-slate-500 truncate">{task.assignee}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
